import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { ProductVersionClaimDataPane } from '@client/components/products-v2/ProductVersionClaimDataPane';
import { ProductVersionClaimFormCard } from '@client/components/products-v2/ProductVersionClaimFormCard';
import { ProductVersionFormFieldsPane } from '@client/components/products-v2/ProductVersionFormFieldsPane';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { trpc } from '@client/trpc/client';
import { isRetryableQueryError } from '@client/trpc/error-classification';
import { MAX_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/fields',
)({
    staticData: {
        ui: {
            useParentTitle: false,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <FieldsWorkspace />
        </PageController>
    );
}

function FieldsWorkspace() {
    const { orgId } = Route.useParams();
    const productVersion = productVersionFormStore.use.formData();

    const productVersionId = productVersion.id;
    const hasFormLinked = !!productVersion.formDefinitionVersion;

    const formFieldsQuery = trpc.productVersion.getPinnedFormFields.useQuery(
        { productVersionId },
        { meta: { errorSurface: 'inline' } },
    );
    const mappingsQuery = trpc.productVersionFieldMapping.listProductVersionFieldMappings.useQuery(
        { productVersionId },
        { meta: { errorSurface: 'inline' } },
    );
    const productFieldsQuery = trpc.fieldConfiguration.listFieldConfigurations.useQuery(
        {
            where: {
                entity: 'PRODUCT_VERSION',
                entityId: productVersionId,
            },
        },
        { meta: { errorSurface: 'inline' } },
    );

    // Contact types are mapping targets too — one option group per (type, instance).
    const contactTypesQuery = trpc.productVersionContactType.listProductVersionContactTypes.useQuery(
        { where: { productVersionId }, limit: MAX_PAGE_SIZE },
        { meta: { errorSurface: 'inline' } },
    );

    const formFields = formFieldsQuery.data?.fields ?? [];
    const mappings = mappingsQuery.data?.rows ?? [];
    const productFields = productFieldsQuery.data?.rows ?? [];
    const contactTypes = contactTypesQuery.data?.rows ?? [];

    const isLoading =
        formFieldsQuery.isPending ||
        mappingsQuery.isPending ||
        productFieldsQuery.isPending ||
        contactTypesQuery.isPending;
    const queryErrors = [formFieldsQuery.error, mappingsQuery.error, productFieldsQuery.error, contactTypesQuery.error];
    const queryError = queryErrors.find((error) => error !== null);

    if (queryError) {
        return (
            <InlineQueryError
                error={queryError}
                retryable={queryErrors.some((error) => error !== null && isRetryableQueryError(error))}
                onRetry={() => {
                    void Promise.all([
                        formFieldsQuery.refetch(),
                        mappingsQuery.refetch(),
                        productFieldsQuery.refetch(),
                        contactTypesQuery.refetch(),
                    ]);
                }}
            />
        );
    }

    return (
        <div className="max-w-7xl space-y-4">
            <ProductVersionClaimFormCard orgId={orgId} formFields={formFields} mappings={mappings} />

            <div className="grid items-start gap-4 xl:grid-cols-2">
                {hasFormLinked && (
                    <ProductVersionFormFieldsPane
                        formFields={formFields}
                        mappings={mappings}
                        productFields={productFields}
                        contactTypes={contactTypes}
                        isLoading={isLoading}
                    />
                )}
                <ProductVersionClaimDataPane
                    formFields={formFields}
                    mappings={mappings}
                    productFields={productFields}
                    contactTypes={contactTypes}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}
